import React, { useEffect, useState } from 'react';

const SERVICE_TYPES = [
  { key: 'ATTORNEY', label: 'Attorney' },
  { key: 'BOND_ORIGINATOR', label: 'Bond Originator' },
  { key: 'CONTRACTOR', label: 'Contractor' },
  { key: 'INSPECTOR', label: 'Inspector' },
  { key: 'OTHER', label: 'Other' }
];

const SPECIALIZATIONS_BY_TYPE = {
  ATTORNEY: ['CONVEYANCING', 'BOND_REGISTRATION', 'PROPERTY_LAW', 'COMMERCIAL', 'LITIGATION'],
  BOND_ORIGINATOR: ['RESIDENTIAL', 'COMMERCIAL', 'DEVELOPMENT', 'REFINANCING'],
  CONTRACTOR: ['PLUMBING', 'ELECTRICAL', 'HVAC', 'GENERAL', 'ROOFING', 'PAINTING'],
  INSPECTOR: ['BUILDING', 'ELECTRICAL', 'PLUMBING', 'STRUCTURAL', 'PEST'],
  OTHER: ['GENERAL']
};

const formatSpecLabel = (s) => s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const buildEmptyState = () => ({
  companyName: '',
  tradingName: '',
  type: 'ATTORNEY',
  specializations: [],
  serviceAreasText: '',
  primaryContact: {
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    cellphone: '',
    telephone: '',
    fax: ''
  },
  physicalAddress: {
    number: '',
    street: '',
    suburb: '',
    city: '',
    province: '',
    postalCode: '',
    complexName: '',
    unitNumber: ''
  },
  registrationNumber: '',
  vatNumber: '',
  licenseNumber: '',
  professionalBody: '',
  website: '',
  notes: '',
  active: true
});

const fromService = (svc) => {
  if (!svc) return buildEmptyState();
  const empty = buildEmptyState();
  return {
    ...empty,
    companyName: svc.companyName || '',
    tradingName: svc.tradingName || '',
    type: svc.type || 'ATTORNEY',
    specializations: Array.isArray(svc.specializations) ? [...svc.specializations] : [],
    serviceAreasText: Array.isArray(svc.serviceAreas) ? svc.serviceAreas.join(', ') : '',
    primaryContact: { ...empty.primaryContact, ...(svc.primaryContact || {}) },
    physicalAddress: { ...empty.physicalAddress, ...(svc.physicalAddress || {}) },
    registrationNumber: svc.registrationNumber || '',
    vatNumber: svc.vatNumber || '',
    licenseNumber: svc.licenseNumber || '',
    professionalBody: svc.professionalBody || '',
    website: svc.website || '',
    notes: svc.notes || '',
    active: svc.active !== false
  };
};

const stripEmpty = (obj) => {
  const out = {};
  Object.entries(obj).forEach(([k, v]) => {
    if (k === '__typename') return;
    if (v !== '' && v !== null && v !== undefined) out[k] = v;
  });
  return out;
};

export const buildMutationInput = (state) => {
  const serviceAreas = state.serviceAreasText
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const physicalAddress = stripEmpty(state.physicalAddress);
  const primaryContact = stripEmpty(state.primaryContact);

  const input = {
    companyName: state.companyName.trim(),
    type: state.type,
    specializations: state.specializations,
    serviceAreas,
    primaryContact,
    physicalAddress,
    active: state.active
  };

  if (state.tradingName.trim()) input.tradingName = state.tradingName.trim();
  if (state.registrationNumber.trim()) input.registrationNumber = state.registrationNumber.trim();
  if (state.vatNumber.trim()) input.vatNumber = state.vatNumber.trim();
  if (state.licenseNumber.trim()) input.licenseNumber = state.licenseNumber.trim();
  if (state.professionalBody.trim()) input.professionalBody = state.professionalBody.trim();
  if (state.website.trim()) input.website = state.website.trim();
  if (state.notes.trim()) input.notes = state.notes.trim();

  return input;
};

export const validateForm = (state) => {
  const errors = {};
  if (!state.companyName.trim()) errors.companyName = 'Company name is required';
  if (!state.type) errors.type = 'Service type is required';

  const pc = state.primaryContact;
  if (!pc.firstName.trim()) errors['primaryContact.firstName'] = 'First name is required';
  if (!pc.lastName.trim()) errors['primaryContact.lastName'] = 'Last name is required';
  if (!pc.email.trim()) {
    errors['primaryContact.email'] = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(pc.email)) {
    errors['primaryContact.email'] = 'Email is invalid';
  }
  if (!pc.cellphone.trim()) errors['primaryContact.cellphone'] = 'Cellphone is required';

  const addr = state.physicalAddress;
  if (!addr.street.trim()) errors['physicalAddress.street'] = 'Street is required';
  if (!addr.suburb.trim()) errors['physicalAddress.suburb'] = 'Suburb is required';
  if (!addr.city.trim()) errors['physicalAddress.city'] = 'City is required';

  return errors;
};

const ProfessionalServiceForm = ({
  initialService = null,
  onSubmit,
  submitting = false,
  submitLabel = 'Save',
  onCancel
}) => {
  const [state, setState] = useState(() => fromService(initialService));
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialService) setState(fromService(initialService));
  }, [initialService]);

  const setField = (path, value) => {
    setState((prev) => {
      const next = { ...prev };
      const parts = path.split('.');
      if (parts.length === 1) {
        next[parts[0]] = value;
      } else {
        next[parts[0]] = { ...prev[parts[0]], [parts[1]]: value };
      }
      return next;
    });
    if (errors[path]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[path];
        return next;
      });
    }
  };

  const handleTypeChange = (newType) => {
    const allowed = new Set(SPECIALIZATIONS_BY_TYPE[newType] || []);
    setState((prev) => ({
      ...prev,
      type: newType,
      specializations: prev.specializations.filter((s) => allowed.has(s))
    }));
  };

  const toggleSpecialization = (spec) => {
    setState((prev) => {
      const has = prev.specializations.includes(spec);
      return {
        ...prev,
        specializations: has
          ? prev.specializations.filter((s) => s !== spec)
          : [...prev.specializations, spec]
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const v = validateForm(state);
    setErrors(v);
    if (Object.keys(v).length === 0) {
      onSubmit(buildMutationInput(state));
    }
  };

  const inputClass = (key) =>
    `block w-full px-3 py-2 border ${errors[key] ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`;

  const fieldError = (key) =>
    errors[key] ? <p className="mt-1 text-sm text-red-600">{errors[key]}</p> : null;

  const availableSpecs = SPECIALIZATIONS_BY_TYPE[state.type] || [];

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Basic Information */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name*</label>
            <input
              type="text"
              value={state.companyName}
              onChange={(e) => setField('companyName', e.target.value)}
              className={inputClass('companyName')}
            />
            {fieldError('companyName')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trading Name</label>
            <input
              type="text"
              value={state.tradingName}
              onChange={(e) => setField('tradingName', e.target.value)}
              className={inputClass('tradingName')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Service Type*</label>
            <select
              value={state.type}
              onChange={(e) => handleTypeChange(e.target.value)}
              className={inputClass('type')}
            >
              {SERVICE_TYPES.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
            {fieldError('type')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={state.active ? 'true' : 'false'}
              onChange={(e) => setField('active', e.target.value === 'true')}
              className={inputClass('active')}
            >
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        {availableSpecs.length > 0 && (
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">Specializations</label>
            <div className="flex flex-wrap gap-2">
              {availableSpecs.map((spec) => {
                const checked = state.specializations.includes(spec);
                return (
                  <button
                    type="button"
                    key={spec}
                    onClick={() => toggleSpecialization(spec)}
                    className={`px-3 py-1 rounded-full text-xs font-medium border ${
                      checked
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {formatSpecLabel(spec)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Service Areas</label>
          <input
            type="text"
            value={state.serviceAreasText}
            onChange={(e) => setField('serviceAreasText', e.target.value)}
            placeholder="Comma-separated, e.g. George, Mossel Bay, Knysna"
            className={inputClass('serviceAreasText')}
          />
        </div>
      </section>

      {/* Primary Contact */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Primary Contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={state.primaryContact.title}
              onChange={(e) => setField('primaryContact.title', e.target.value)}
              className={inputClass('primaryContact.title')}
            />
          </div>
          <div className="hidden md:block" />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name*</label>
            <input
              type="text"
              value={state.primaryContact.firstName}
              onChange={(e) => setField('primaryContact.firstName', e.target.value)}
              className={inputClass('primaryContact.firstName')}
            />
            {fieldError('primaryContact.firstName')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name*</label>
            <input
              type="text"
              value={state.primaryContact.lastName}
              onChange={(e) => setField('primaryContact.lastName', e.target.value)}
              className={inputClass('primaryContact.lastName')}
            />
            {fieldError('primaryContact.lastName')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
            <input
              type="email"
              value={state.primaryContact.email}
              onChange={(e) => setField('primaryContact.email', e.target.value)}
              className={inputClass('primaryContact.email')}
            />
            {fieldError('primaryContact.email')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cellphone*</label>
            <input
              type="tel"
              value={state.primaryContact.cellphone}
              onChange={(e) => setField('primaryContact.cellphone', e.target.value)}
              className={inputClass('primaryContact.cellphone')}
            />
            {fieldError('primaryContact.cellphone')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Telephone</label>
            <input
              type="tel"
              value={state.primaryContact.telephone}
              onChange={(e) => setField('primaryContact.telephone', e.target.value)}
              className={inputClass('primaryContact.telephone')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fax</label>
            <input
              type="tel"
              value={state.primaryContact.fax}
              onChange={(e) => setField('primaryContact.fax', e.target.value)}
              className={inputClass('primaryContact.fax')}
            />
          </div>
        </div>
      </section>

      {/* Physical Address */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Physical Address</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Number</label>
            <input
              type="text"
              value={state.physicalAddress.number}
              onChange={(e) => setField('physicalAddress.number', e.target.value)}
              className={inputClass('physicalAddress.number')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Street*</label>
            <input
              type="text"
              value={state.physicalAddress.street}
              onChange={(e) => setField('physicalAddress.street', e.target.value)}
              className={inputClass('physicalAddress.street')}
            />
            {fieldError('physicalAddress.street')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Suburb*</label>
            <input
              type="text"
              value={state.physicalAddress.suburb}
              onChange={(e) => setField('physicalAddress.suburb', e.target.value)}
              className={inputClass('physicalAddress.suburb')}
            />
            {fieldError('physicalAddress.suburb')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City*</label>
            <input
              type="text"
              value={state.physicalAddress.city}
              onChange={(e) => setField('physicalAddress.city', e.target.value)}
              className={inputClass('physicalAddress.city')}
            />
            {fieldError('physicalAddress.city')}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Province</label>
            <input
              type="text"
              value={state.physicalAddress.province}
              onChange={(e) => setField('physicalAddress.province', e.target.value)}
              className={inputClass('physicalAddress.province')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code</label>
            <input
              type="text"
              value={state.physicalAddress.postalCode}
              onChange={(e) => setField('physicalAddress.postalCode', e.target.value)}
              className={inputClass('physicalAddress.postalCode')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Complex Name</label>
            <input
              type="text"
              value={state.physicalAddress.complexName}
              onChange={(e) => setField('physicalAddress.complexName', e.target.value)}
              className={inputClass('physicalAddress.complexName')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit Number</label>
            <input
              type="text"
              value={state.physicalAddress.unitNumber}
              onChange={(e) => setField('physicalAddress.unitNumber', e.target.value)}
              className={inputClass('physicalAddress.unitNumber')}
            />
          </div>
        </div>
      </section>

      {/* Compliance & Other */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Registration & Compliance</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
            <input
              type="text"
              value={state.registrationNumber}
              onChange={(e) => setField('registrationNumber', e.target.value)}
              className={inputClass('registrationNumber')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number</label>
            <input
              type="text"
              value={state.vatNumber}
              onChange={(e) => setField('vatNumber', e.target.value)}
              className={inputClass('vatNumber')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
            <input
              type="text"
              value={state.licenseNumber}
              onChange={(e) => setField('licenseNumber', e.target.value)}
              className={inputClass('licenseNumber')}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Professional Body</label>
            <input
              type="text"
              value={state.professionalBody}
              onChange={(e) => setField('professionalBody', e.target.value)}
              className={inputClass('professionalBody')}
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
            <input
              type="url"
              value={state.website}
              onChange={(e) => setField('website', e.target.value)}
              placeholder="https://"
              className={inputClass('website')}
            />
          </div>
        </div>
      </section>

      {/* Notes */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
        <textarea
          rows={4}
          value={state.notes}
          onChange={(e) => setField('notes', e.target.value)}
          className={inputClass('notes')}
        />
      </section>

      <div className="flex items-center justify-end space-x-3 pt-4 border-t">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
};

export default ProfessionalServiceForm;
